import { useState } from "react";
import { ImageUp, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { fixMyRelease, uploadEpkImage, resolveAsset } from "@/lib/afmApi";
import { useAuth } from "@/context/AuthContext";
import { CoverEditorModal } from "@/components/CoverEditorModal";

// Corregir lo que pidió la revisión automática: portada nueva, título y (solo el equipo) el artista principal.
// Al enviar se revisa otra vez; si queda bien y el modo es automático, sale sola a la distribuidora.
export default function ReleaseFixPanel({ releaseId, title, artist = "", date = "", migration = false, prevLabel = "", admin = false, onDone }) {
  const { user } = useAuth();
  const [cover, setCover] = useState("");
  const [name, setName] = useState(title || "");
  const [art, setArt] = useState(artist || "");
  const [day, setDay] = useState((date || "").slice(0, 10));
  const [prev, setPrev] = useState(prevLabel || "");
  const [busy, setBusy] = useState(false);
  const [editorFile, setEditorFile] = useState(null);
  const errText = (e) => e?.response?.data?.detail || "No se pudo completar";

  const attach = async (b64, type) => {
    setBusy(true);
    try {
      const up = await uploadEpkImage(user.id, type, b64);
      const url = resolveAsset(up?.url);
      if (!url) throw new Error("sin url");
      setCover(url);
      toast.success("Portada nueva lista");
    } catch (e) { toast.error("No se pudo subir la portada"); } finally { setBusy(false); }
  };
  const pick = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Formato no válido. Usa JPG, PNG o WEBP."); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      const w = img.naturalWidth, h = img.naturalHeight; URL.revokeObjectURL(url);
      if (w === h && w >= 3000 && w <= 6000) {
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.readAsDataURL(file); });
        attach(b64, file.type);
      } else setEditorFile(file); // la ajustamos a 3000×3000
    };
    img.onerror = () => { URL.revokeObjectURL(url); setEditorFile(file); };
    img.src = url;
  };
  const send = async () => {
    const body = {};
    if (cover) body.cover_url = cover;
    if (name.trim() && name.trim() !== (title || "")) body.title = name.trim();
    if (admin && art.trim() && art.trim() !== (artist || "")) body.main_artist = art.trim();
    if (admin && day && day !== (date || "").slice(0, 10)) body.release_date = day;
    if (migration && prev.trim() && prev.trim() !== (prevLabel || "")) body.previous_label = prev.trim();
    if (!Object.keys(body).length) { toast.error(admin ? "Cambia algo: portada, artista, título o fecha" : "Sube la portada corregida o cambia el título"); return; }
    setBusy(true);
    try {
      const r = await fixMyRelease(releaseId, body);
      const st = r?.review?.status;
      const sent = r?.result?.send === "sent";
      if (st === "needs_fix") toast.error("Lo revisamos otra vez y todavía falta algo: mira la lista");
      else if (admin) toast.success(st === "manual" ? "Guardado. La revisión pide que lo decidas tú: usa Revisar envío" : sent ? "Corregido y enviado a la distribuidora" : "Corregido y aprobado. Envíalo con Revisar envío");
      else toast.success(st === "manual" ? "Corrección enviada: el equipo le da el último vistazo" : "¡Corregido! Tu lanzamiento sigue su camino");
      onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-red-200 bg-white p-3 text-slate-800" data-testid="review-fix-panel">
      <div className="text-xs font-semibold uppercase tracking-wider text-red-700">{admin ? "Corregir y volver a revisar" : "Corrígelo aquí"}</div>
      <div className="flex flex-wrap items-center gap-3">
        {cover ? <img src={cover} alt="Portada nueva" className="h-16 w-16 rounded-md object-cover" /> : null}
        <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:border-black">
          <ImageUp className="h-4 w-4" /> {cover ? "Cambiar portada nueva" : "Subir portada corregida"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        <span className="text-xs text-slate-500">JPG o PNG cuadrada, 3000×3000. Sin logos, sin URLs y con el nombre igual al del artista.</span>
      </div>
      {admin && (
        <label className="block text-xs font-semibold text-slate-500">Artista principal
          <input value={art} onChange={(e) => setArt(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-black outline-none focus:border-black" data-testid="review-fix-artist" />
        </label>
      )}
      {admin && (
        <label className="block text-xs font-semibold text-slate-500">Fecha de salida
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-black outline-none focus:border-black" data-testid="review-fix-date" />
          <span className="mt-1 block font-normal text-slate-400">Al menos 3 días después de hoy. En migraciones no hace falta: sale sola con la primera fecha válida.</span>
        </label>
      )}
      {migration && (
        <label className="block text-xs font-semibold text-slate-500">Distribuidora anterior
          <input value={prev} onChange={(e) => setPrev(e.target.value)} placeholder="Ej. DistroKid, ONErpm, CD Baby…" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-black outline-none focus:border-black" data-testid="review-fix-prev" />
          <span className="mt-1 block font-normal text-slate-400">Dónde estaba publicada antes esta canción.</span>
        </label>
      )}
      <label className="block text-xs font-semibold text-slate-500">Título del lanzamiento
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-black outline-none focus:border-black" />
      </label>
      <button onClick={send} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="review-fix-send">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {admin ? "Guardar y revisar" : "Enviar corrección"}
      </button>
      <p className="text-[11px] text-slate-500">{admin ? "Se guarda en SONA y en el envío, y se revisa de nuevo al instante. Si queda bien y el modo es automático, sale solo y te llega el correo." : "El nombre del artista no se cambia aquí: debe ser tu perfil vinculado. Si el problema es otro dato, escríbenos a soporte."}</p>
      {editorFile && <CoverEditorModal file={editorFile} onApply={(res) => { setEditorFile(null); const b64 = String(res?.image || "").split(",")[1]; if (b64) attach(b64, "image/jpeg"); else toast.error("No se pudo ajustar la portada"); }} onCancel={() => setEditorFile(null)} />}
    </div>
  );
}
